import { Card } from "@/components/ui/card";
import { TrendingUp, Zap, Target, Bot, CheckCircle2, BarChart3 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";

const WhatsNext = () => {
  const { t } = useTranslation();
  
  const features = [
    {
      icon: TrendingUp,
      title: t('whatsNext.features.analytics.title'),
      description: t('whatsNext.features.analytics.description')
    },
    {
      icon: Zap,
      title: t('whatsNext.features.aiSignals.title'),
      description: t('whatsNext.features.aiSignals.description').split(':X AI')[0] + ':X AI' + t('whatsNext.features.aiSignals.description').split(':X AI')[1]
    },
    {
      icon: Target,
      title: t('whatsNext.features.curation.title'),
      description: t('whatsNext.features.curation.description')
    },
    {
      icon: Bot,
      title: t('whatsNext.features.autoTrade.title'),
      description: t('whatsNext.features.autoTrade.description')
    }
  ];

  return (
    <section className="py-20 relative">
      <div className="container px-4">
        {/* Coming Soon Section */}
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-primary rounded-full mb-4">
            <span className="text-sm font-bold">{t('whatsNext.badge')}</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-bold">
            {t('whatsNext.title').split('<span>')[0]}
            <span className="bg-gradient-primary bg-clip-text text-transparent">
              {t('whatsNext.title').split('<span>')[1]?.split('</span>')[0]}
            </span>
            {t('whatsNext.title').split('</span>')[1]}
          </h2>
          <p className="text-xl text-muted-foreground">
            {t('whatsNext.subtitle')}
          </p>
        </div>

        {/* Now Live Section */}
        <div className="text-center max-w-5xl mx-auto mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/30 rounded-full mb-6 animate-pulse">
            <CheckCircle2 className="w-5 h-5 text-green-500" />
            <span className="text-sm font-bold text-green-500">NOW LIVE</span>
          </div>
          <Card className="p-8 md:p-12 bg-gradient-primary relative overflow-hidden border-2 border-green-500/30">
            <div className="absolute inset-0 bg-card/90 backdrop-blur-2xl" />
            <div className="relative z-10 space-y-6">
              <div className="flex items-center justify-center gap-4">
                <div className="p-4 bg-gradient-primary rounded-2xl">
                  <BarChart3 className="w-12 h-12" />
                </div>
                <h2 className="text-4xl md:text-5xl font-bold">
                  Dashboard <span className="bg-gradient-primary bg-clip-text text-transparent">v1.0</span>
                </h2>
              </div>
              <h3 className="text-2xl md:text-3xl font-semibold">Live Performance Analytics</h3>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                Real-time tracking and transparency for every signal. Watch your portfolio grow with detailed metrics, win rates, and ROI tracking across all trader signals. Based on the Oraculus:X trading strategy or your own.
              </p>
              <div className="flex flex-wrap justify-center gap-4 pt-4">
                <div className="px-4 py-2 bg-card/50 rounded-lg border border-primary/20">
                  <span className="text-sm font-semibold">✓ Real-Time Tracking</span>
                </div>
                <div className="px-4 py-2 bg-card/50 rounded-lg border border-primary/20">
                  <span className="text-sm font-semibold">✓ Win Rate Analytics</span>
                </div>
                <div className="px-4 py-2 bg-card/50 rounded-lg border border-primary/20">
                  <span className="text-sm font-semibold">✓ ROI Monitoring</span>
                </div>
                <div className="px-4 py-2 bg-card/50 rounded-lg border border-primary/20">
                  <span className="text-sm font-semibold">✓ Portfolio Growth</span>
                </div>
              </div>
              <Button 
                size="lg" 
                className="bg-gradient-primary hover:shadow-glow-primary transition-all duration-300 text-lg px-8"
                asChild
              >
                <a href="https://dashboard.oraculusx.com" target="_blank" rel="noopener noreferrer">
                  Explore Dashboard v1.0
                </a>
              </Button>
              <p className="text-sm text-muted-foreground">
                🔒 Available exclusively for premium members
              </p>
            </div>
          </Card>
        </div>

        <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto">
          {features.map((feature, index) => (
            <Card 
              key={index}
              className="p-8 bg-card/50 backdrop-blur border border-primary/20 hover:border-primary/40 transition-all duration-300 hover:shadow-glow-primary group"
            >
              <div className="space-y-4">
                <div className="w-14 h-14 rounded-lg bg-gradient-primary flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <feature.icon className="w-7 h-7 text-primary-foreground" />
                </div>
                <h3 className="text-2xl font-bold">{feature.title}</h3>
                <p className="text-muted-foreground leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </Card>
          ))}
        </div>

        <div className="text-center mt-12">
          <p className="text-lg text-muted-foreground">
            {t('whatsNext.footer')}
          </p>
        </div>
      </div>

      {/* Background decoration */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-opal rounded-full blur-3xl opacity-10 pointer-events-none" />
    </section>
  );
};

export default WhatsNext;
